import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, QrCode, MessageSquareText, LayoutDashboard, LogOut, Plus,
  Rocket, ScanLine, Send, Loader2, Copy, ExternalLink, Bell, CheckCircle2, Inbox, ArrowLeft, Trash2,
} from "lucide-react";
import { api, wsUrl, getSession, setSession, clearSession } from "@/lib/api";
import { BrandLogo, BrandMark } from "@/components/ui/brand-logo";
import { ChatBubble } from "@/components/ui/chat-bubble";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const COUNTRIES = [
  "Canada", "United States", "United Kingdom", "Australia", "New Zealand", "Ireland",
  "India", "Germany", "France", "Spain", "Italy", "Netherlands", "Belgium", "Switzerland",
  "Sweden", "Norway", "Denmark", "Finland", "Portugal", "Austria", "Poland", "Greece",
  "Mexico", "Brazil", "Argentina", "Chile", "Colombia", "United Arab Emirates", "Saudi Arabia",
  "South Africa", "Nigeria", "Kenya", "Egypt", "Japan", "China", "South Korea", "Singapore",
  "Malaysia", "Indonesia", "Philippines", "Thailand", "Vietnam", "Pakistan", "Bangladesh",
  "Sri Lanka", "Turkey", "Israel", "Qatar", "Other",
];

const fmtDate = (iso) => new Date(iso).toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const CONSENT = "I agree to receive ReachMeNow Indiegogo updates, product news, beta invitations, promotions, and future emails. I understand I can unsubscribe at any time.";

/* ---------------- Signup ---------------- */
function Signup({ onDone, waitlist }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!country) { toast.error("Please select your country."); return; }
    if (!consent) { toast.error("Please agree to receive updates to continue."); return; }
    setLoading(true);
    try {
      const r = await api.post("/subscribers/register", { full_name: fullName, email, country, consent });
      setSession(r.data);
      onDone(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200/60">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center">
          <button onClick={() => (window.location.href = "/")} data-testid="signup-home-link"><BrandLogo /></button>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center p-5">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange bg-orange-50 px-3 py-1.5 rounded-full">
              <Rocket className="h-3.5 w-3.5" /> {waitlist ? "Join the waitlist" : "Try the demo free"}
            </span>
            <h1 className="mt-5 font-heading font-extrabold text-3xl tracking-tight">Create your demo account</h1>
            <p className="mt-2 text-slate-500">Just your name, email and country. No phone number needed — ever.</p>
          </div>
          <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-7 shadow-sm space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full Name</Label>
              <Input id="name" required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Alex Rivera" className="h-11 focus-visible:ring-brand-blue" data-testid="signup-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@example.com" className="h-11 focus-visible:ring-brand-blue" data-testid="signup-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="h-11 focus:ring-brand-blue" data-testid="signup-country-select">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={setConsent} className="mt-0.5 data-[state=checked]:bg-brand-blue data-[state=checked]:border-brand-blue" data-testid="signup-consent-checkbox" />
              <span className="text-xs text-slate-500 leading-relaxed">{CONSENT}</span>
            </label>
            <Button type="submit" disabled={loading} className="w-full bg-brand-orange hover:bg-brand-orangehover h-12 text-base" data-testid="signup-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join Demo"}
            </Button>
            <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> We respect your privacy.</p>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

/* ---------------- QR Card ---------------- */
function QRCard({ qr, onCopy, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(qr.id);
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`qr-card-${qr.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold">{qr.label}</h3>
          <p className="text-xs text-slate-400">Created {fmtDate(qr.created_at)}</p>
        </div>
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{qr.status}</Badge>
      </div>
      <div className="mt-4 flex justify-center bg-slate-50 rounded-xl p-3 border border-slate-100">
        <img src={qr.image} alt={qr.label} className="h-36 w-36" data-testid={`qr-image-${qr.id}`} />
      </div>
      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-slate-500"><ScanLine className="h-4 w-4" /> {qr.scans} scans</span>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-slate-500 hover:text-brand-blue" onClick={() => onCopy(qr.contact_url)} data-testid={`qr-copy-${qr.id}`}><Copy className="h-3.5 w-3.5" /> Copy</Button>
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-slate-500 hover:text-brand-blue" onClick={() => window.open(qr.contact_url, "_blank")} data-testid={`qr-open-${qr.id}`}><ExternalLink className="h-3.5 w-3.5" /> Open</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 gap-1 text-slate-500 hover:text-red-600" data-testid={`qr-delete-${qr.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="qr-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this QR code?</AlertDialogTitle>
                <AlertDialogDescription>
                  Deleting this QR code will permanently delete all messages received through it. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="qr-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="qr-delete-confirm">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Messages / Inbox ---------------- */
function Messages({ sub, onUnreadChange }) {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const convWsRef = useRef(null);
  const endRef = useRef(null);

  const activeRef = useRef(null);
  const loadConvs = () => api.get("/conversations", { params: { subscriber_id: sub.id } }).then((r) => {
    const aid = activeRef.current;
    const list = aid ? r.data.map((c) => (c.id === aid ? { ...c, unread: 0 } : c)) : r.data;
    setConvs(list);
    onUnreadChange?.(list.reduce((a, c) => a + (c.unread || 0), 0));
  });

  useEffect(() => {
    loadConvs();
    const ws = new WebSocket(wsUrl(`owner:${sub.id}`));
    ws.onmessage = () => loadConvs();
    return () => ws.close();
    // eslint-disable-next-line
  }, [sub.id]);

  const openConv = async (c) => {
    setActive(c);
    activeRef.current = c.id;
    const r = await api.get(`/conversations/${c.id}/messages`);
    setMessages(r.data);
    await api.post(`/conversations/${c.id}/read`);
    loadConvs();
    convWsRef.current?.close();
    const ws = new WebSocket(wsUrl(`conv:${c.id}`));
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "message") {
        setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
        if (data.message.sender === "visitor") api.post(`/conversations/${c.id}/read`);
      }
    };
    convWsRef.current = ws;
  };

  useEffect(() => () => convWsRef.current?.close(), []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    const r = await api.post(`/conversations/${active.id}/reply`, { text: reply.trim() });
    setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
    setReply("");
    loadConvs();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden grid md:grid-cols-[320px_1fr] h-[calc(100vh-11rem)]">
      {/* Conversation list */}
      <div className={`border-r border-slate-100 overflow-y-auto no-scrollbar ${active ? "hidden md:block" : "block"}`} data-testid="conversation-list">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-heading font-bold flex items-center gap-2"><Inbox className="h-4 w-4 text-brand-blue" /> Conversations</h3>
        </div>
        {convs.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No messages yet. Share a QR code and conversations will appear here.</div>
        ) : convs.map((c) => (
          <button key={c.id} onClick={() => openConv(c)} className={`w-full text-left px-4 py-3.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${active?.id === c.id ? "bg-blue-50/60" : ""}`} data-testid={`conversation-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm truncate">{c.visitor_name}</span>
              <span className="text-[11px] text-slate-400 shrink-0">{fmtTime(c.last_message_at)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 mt-0.5">
              <p className="text-xs text-slate-500 truncate">{c.last_message}</p>
              {c.unread > 0 && <span className="shrink-0 h-5 min-w-5 px-1.5 rounded-full bg-brand-orange text-white text-[11px] font-semibold flex items-center justify-center">{c.unread}</span>}
            </div>
            <p className="text-[11px] text-slate-300 mt-0.5">{c.label}</p>
          </button>
        ))}
      </div>

      {/* Chat window */}
      <div className={`flex flex-col ${active ? "flex" : "hidden md:flex"}`}>
        {active ? (
          <>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
              <Button variant="ghost" size="sm" className="md:hidden px-2" onClick={() => { setActive(null); activeRef.current = null; }} data-testid="chat-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
              <div className="h-9 w-9 rounded-full bg-blue-50 flex items-center justify-center text-brand-blue font-bold text-sm">{active.visitor_name?.[0] || "V"}</div>
              <div>
                <p className="font-semibold text-sm">{active.visitor_name}</p>
                <p className="text-[11px] text-green-600 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Active · {active.label}</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-3 bg-slate-50/40" data-testid="chat-messages">
              {messages.map((m) => <ChatBubble key={m.id} text={m.text} isOwner={m.sender === "owner"} time={fmtTime(m.timestamp)} />)}
              <div ref={endRef} />
            </div>
            <div className="border-t border-slate-100 p-3 flex items-end gap-2">
              <Textarea value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }} placeholder="Type your reply…" rows={1} className="resize-none min-h-[44px] max-h-28 rounded-2xl focus-visible:ring-brand-blue" data-testid="chat-reply-input" />
              <Button onClick={sendReply} disabled={!reply.trim()} className="bg-brand-blue hover:bg-brand-bluehover rounded-full h-11 w-11 p-0 shrink-0" data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center"><MessageSquareText className="h-7 w-7 text-brand-blue" /></div>
            <p className="mt-4 text-slate-500 max-w-xs">Select a conversation to view messages and reply in real time.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard({ sub, setSub, initialTab }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab || "overview");
  const [qrs, setQrs] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);

  const loadQrs = () => api.get("/qr", { params: { subscriber_id: sub.id } }).then((r) => setQrs(r.data));
  const loadUnread = () => api.get("/conversations", { params: { subscriber_id: sub.id } }).then((r) => setTotalUnread(r.data.reduce((a, c) => a + (c.unread || 0), 0)));

  const tabRef = useRef(tab);
  tabRef.current = tab;

  useEffect(() => {
    loadQrs();
    loadUnread();
    const ws = new WebSocket(wsUrl(`owner:${sub.id}`));
    ws.onmessage = () => { if (tabRef.current !== "messages") loadUnread(); };
    return () => ws.close();
    // eslint-disable-next-line
  }, [sub.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab !== "messages") loadUnread(); }, [tab]);

  const generateQr = async () => {
    if (qrs.length >= 5) {
      toast.error("You've reached the limit of 5 QR codes. Please delete an existing code to generate a new one.");
      return;
    }
    setGenerating(true);
    try {
      await api.post("/qr", { subscriber_id: sub.id, origin: window.location.origin });
      await loadQrs();
      setTab("qr");
      toast.success("Demo QR code generated!");
    } finally {
      setGenerating(false);
    }
  };

  const deleteQr = async (qrId) => {
    await api.delete(`/qr/${qrId}`);
    await loadQrs();
    await loadUnread();
    toast.success("QR code deleted");
  };

  const atLimit = qrs.length >= 5;

  const notifyKickstarter = async () => {
    const r = await api.post(`/subscribers/${sub.id}/kickstarter`);
    setSub(r.data);
    setSession(r.data);
    toast.success("You'll be notified about Indiegogo updates!");
  };

  const logout = () => { clearSession(); navigate("/"); };

  const nav = [
    { key: "overview", label: "Dashboard", icon: LayoutDashboard },
    { key: "qr", label: "My QR Codes", icon: QrCode },
    { key: "messages", label: "Messages", icon: MessageSquareText, badge: totalUnread },
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] font-body flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-slate-200 bg-white p-4 sticky top-0 h-screen">
        <div className="px-2 py-2"><BrandLogo /></div>
        <nav className="mt-6 space-y-1">
          {nav.map((n) => (
            <button key={n.key} onClick={() => setTab(n.key)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === n.key ? "bg-blue-50 text-brand-blue" : "text-slate-500 hover:bg-slate-50"}`} data-testid={`nav-${n.key}`}>
              <n.icon className="h-4.5 w-4.5" /> {n.label}
              {n.badge > 0 && <span className="ml-auto h-5 min-w-5 px-1.5 rounded-full bg-brand-orange text-white text-[11px] font-semibold flex items-center justify-center">{n.badge > 99 ? "99+" : n.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="mt-auto border-t border-slate-100 pt-4">
          <div className="px-3 py-2">
            <p className="font-semibold text-sm truncate">{sub.full_name}</p>
            <p className="text-xs text-slate-400 truncate">{sub.email}</p>
          </div>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-50" data-testid="logout-btn"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex">
        {nav.map((n) => (
          <button key={n.key} onClick={() => setTab(n.key)} className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-semibold relative ${tab === n.key ? "text-brand-blue" : "text-slate-400"}`} data-testid={`mnav-${n.key}`}>
            <n.icon className="h-5 w-5" />{n.label.split(" ")[0]}
            {n.badge > 0 && <span className="absolute top-1 right-[28%] h-4 min-w-4 px-1 rounded-full bg-brand-orange text-white text-[10px] flex items-center justify-center">{n.badge > 99 ? "99+" : n.badge}</span>}
          </button>
        ))}
      </div>

      {/* Main */}
      <main className="flex-1 p-5 md:p-8 pb-24 md:pb-8 overflow-x-hidden">
        <div className="md:hidden mb-4"><BrandLogo /></div>

        {tab === "overview" && (
          <div className="max-w-4xl">
            {/* Waitlist banner */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-3xl bg-brand-navy text-white p-8 md:p-10" data-testid="waitlist-banner">
              <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-brand-orange/30 blur-3xl" />
              <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-brand-blue/40 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-2 text-brand-orange text-xs font-semibold uppercase tracking-[0.2em]"><CheckCircle2 className="h-4 w-4" /> Welcome, {sub.full_name.split(" ")[0]}</div>
                <h1 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl tracking-tight">You're on the ReachMeNow waitlist!</h1>
                <p className="mt-3 text-slate-300 max-w-lg">Try the demo now — generate your private QR code and experience real-time messaging without sharing your number.</p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Button onClick={generateQr} disabled={generating} className="bg-brand-blue hover:bg-brand-bluehover rounded-full h-12 px-6" data-testid="overview-generate-qr-btn">
                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><QrCode className="mr-1.5 h-4 w-4" /> Generate Demo QR Code</>}
                  </Button>
                  <Button onClick={notifyKickstarter} disabled={sub.kickstarter_interested} variant="outline" className="rounded-full h-12 px-6 border-white/30 bg-white/5 text-white hover:bg-white/15" data-testid="overview-notify-btn">
                    {sub.kickstarter_interested ? <><CheckCircle2 className="mr-1.5 h-4 w-4" /> You'll be notified</> : <><Bell className="mr-1.5 h-4 w-4" /> Notify Me About Indiegogo Updates</>}
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Stats */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: "QR codes", value: qrs.length, icon: QrCode, color: "text-brand-blue", bg: "bg-blue-50" },
                { label: "Total scans", value: qrs.reduce((a, q) => a + q.scans, 0), icon: ScanLine, color: "text-brand-orange", bg: "bg-orange-50" },
                { label: "Unread messages", value: totalUnread, icon: MessageSquareText, color: "text-brand-blue", bg: "bg-blue-50" },
              ].map((c) => (
                <div key={c.label} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                  <div className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon className={`h-5 w-5 ${c.color}`} /></div>
                  <p className="mt-3 font-heading font-extrabold text-2xl">{c.value}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{c.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "qr" && (
          <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">My QR Codes</h1>
                <p className="text-slate-500 mt-1">Every code links privately to you at /contact.</p>
              </div>
              <Button onClick={generateQr} disabled={generating || atLimit} className="bg-brand-orange hover:bg-brand-orangehover h-11 gap-2 disabled:opacity-50" data-testid="qr-generate-btn">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Generate New</>}
              </Button>
            </div>
            {atLimit && (
              <p className="mt-3 text-sm text-brand-orange font-medium" data-testid="qr-limit-message">
                You've reached the limit of 5 QR codes. Please delete an existing code to generate a new one.
              </p>
            )}
            {qrs.length === 0 ? (
              <div className="mt-8 bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center" data-testid="qr-empty-state">
                <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto"><QrCode className="h-7 w-7 text-brand-blue" /></div>
                <h3 className="mt-4 font-heading font-bold text-lg">No QR codes yet</h3>
                <p className="mt-1 text-slate-500">Generate your first demo QR code to get started.</p>
                <Button onClick={generateQr} disabled={generating} className="mt-5 bg-brand-blue hover:bg-brand-bluehover" data-testid="qr-empty-generate-btn">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Demo QR Code"}
                </Button>
              </div>
            ) : (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {qrs.map((qr) => <QRCard key={qr.id} qr={qr} onCopy={(u) => { navigator.clipboard.writeText(u); toast.success("Link copied"); }} onDelete={deleteQr} />)}
              </div>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-5">Messages</h1>
            <Messages sub={sub} onUnreadChange={setTotalUnread} />
          </div>
        )}
      </main>
    </div>
  );
}

/* ---------------- Root ---------------- */
export default function Demo() {
  const [sub, setSub] = useState(getSession());
  const [params] = useSearchParams();
  const waitlist = params.get("waitlist") === "1";

  if (!sub) return <Signup onDone={setSub} waitlist={waitlist} />;
  return <Dashboard sub={sub} setSub={setSub} initialTab="overview" />;
}
